import { getReadableImageUrl } from '@/lib/directus'
import Image from 'next/image'
import { sanitizeHTML, toBase64, shimmer } from '@/lib/utils'
import ImagePreloader from '@/components/ImagePreloader'

interface ImageField {
	image_file?: {
		image?: {
			id?: string
			filename_download?: string
		}
	}
	alt?: string
}

interface HomeHeroData {
	heading?: {
		text?: string
		title?: string
	}
	subheading?: {
		text?: string
		title?: string
	}
	image?: ImageField
	mobile_image?: ImageField
	text?: {
		text?: string
	}
}

export const fields = [
	'heading.text',
	'heading.title',
	'subheading.text',
	'subheading.title',
	'image.alt',
	'image.image_file.image.id',
	'image.image_file.image.filename_download',
	'mobile_image.alt',
	'mobile_image.image_file.image.id',
	'mobile_image.image_file.image.filename_download',
	'text.text',
] as const

const buildImageUrl = (image?: ImageField, width?: number) => {
	const id = image?.image_file?.image?.id
	const filename = image?.image_file?.image?.filename_download
	if (!id || !filename) return ''

	return (
		getReadableImageUrl(
			id,
			filename,
			width
				? {
						width,
				  }
				: undefined
		) || ''
	)
}

const buildBlur = (width: number, height: number) =>
	`data:image/svg+xml;base64,${toBase64(shimmer(width, height))}`

export default function HomeHero({ data }: { data?: HomeHeroData }) {
	const desktopImageUrl = buildImageUrl(data?.image, 3840)
	const mobileImageUrl =
		buildImageUrl(data?.mobile_image, 750) || desktopImageUrl

	const imageId = data?.image?.image_file?.image?.id || ''
	const imageFilename = data?.image?.image_file?.image?.filename_download || ''

	const mobileImageId = data?.mobile_image?.image_file?.image?.id || ''
	const mobileImageFilename =
		data?.mobile_image?.image_file?.image?.filename_download || ''

	const imageAlt = data?.image?.alt || 'Hero Background'
	const mobileImageAlt = data?.mobile_image?.alt || imageAlt

	return (
		<>
			{imageId && imageFilename && (
				<ImagePreloader
					imageId={imageId}
					filename={imageFilename}
					width={3840}
					quality={85}
					media='(min-width: 768px)'
				/>
			)}

			{mobileImageId && mobileImageFilename && (
				<ImagePreloader
					imageId={mobileImageId}
					filename={mobileImageFilename}
					width={1920}
					quality={85}
					media='(max-width: 767px)'
				/>
			)}

			<section
				id='home-hero'
				className='relative w-full h-[640px] md:py-0 md:h-[640px]'
			>
				<div className='absolute inset-0 w-full h-full'>
				{desktopImageUrl && (
				<Image
					src={desktopImageUrl}
					alt={imageAlt}
					fill
					className='hidden md:block object-cover object-center'
					sizes='(min-width: 768px) 100vw'
					priority
					fetchPriority='high'
					placeholder='blur'
					quality={85}
					blurDataURL={buildBlur(1920, 1080)}
				/>
				)}

				{mobileImageUrl && (
					<Image
						src={mobileImageUrl}
						alt={mobileImageAlt}
						fill
						className='md:hidden object-cover object-top'
						sizes='100vw'
						priority
						fetchPriority='high'
						placeholder='blur'
						quality={85}
						blurDataURL={buildBlur(768, 700)}
					/>
				)}
				</div>

				<div className='absolute inset-0 container flex items-end md:items-center z-[11] max-md:p-0'>
					<div className='max-md:text-white w-full md:w-[70%] lg:w-1/2 max-md:bg-[rgb(0_0_0_/_70%)] max-md:p-5 md:ml-auto lg:pb-20'>
						<div className='max-w-[540px]'>
							<h1
								className='font-freightBig xl:text-[83px] md:text-[60px] text-[36px] max-md:mb-4'
								dangerouslySetInnerHTML={sanitizeHTML(
									data?.heading?.title ||
										data?.heading?.text ||
										'Dr. Julian De Silva',
								)}
							/>
							<span
								className='block md:text-2xl text-md pb-3 md:pb-5 md:pt-5 md:mt-0'
								dangerouslySetInnerHTML={sanitizeHTML(
									data?.subheading?.title ||
										data?.subheading?.text ||
										"London's Leading Facial Plastic Surgery Practice",
								)}
							/>
							{/* <div
								className='space-y-4 xl:text-xl md:text-lg text-sm [&>ul>li]:list-disc [&>ul>li]:py-1 [&>ul]:list-inside [&>ul]:pl-4'
								dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || '')}
							/> */}
							<div className='space-y-4 xl:text-xl md:text-lg text-sm [&>ul>li]:list-disc [&>ul>li]:py-1 [&>ul]:list-inside [&>ul]:pl-4'>
								World-renowned for his signature natural facelift,
								blepharoplasty and rhinoplasty, Dr De Silva exclusively
								specialises in face and neck procedures at his CQC-regulated
								Harley Street facial plastic surgery clinic.
							</div>
							<div className='hidden md:flex md:mt-7 w-full max-w-7xl flex-row justify-center items-center gap-2 lg:gap-[20px]'>
								<div className='h-full flex-1 min-w-0 w-[50px] lg:w-[140px] lg:flex-initial flex justify-center items-center'>
									<div className='text-left text-black text-md uppercase leading-5 tracking-widest'>
										<span className='text-2xl leading-8 tracking-widest'>
											15+
										</span>
										<br />
										Years of Experience
									</div>
								</div>
								<div className='w-[1px] min-w-[1px] h-[80px] min-h-[80px] bg-black shrink-0 self-center' />
								<div className='h-full flex-1 min-w-0 w-[50px] lg:w-[180px] lg:flex-initial flex justify-center items-center'>
									<div className='text-left text-black text-md uppercase leading-5 tracking-widest'>
										<span className='text-2xl leading-8 tracking-widest'>
											1,000+
										</span>
										<br />
										Five-Star Patient Outcomes
									</div>
								</div>
								<div className='w-[1px] min-w-[1px] h-[80px] min-h-[80px] bg-black shrink-0 self-center' />
								<div className='h-full flex-1 min-w-0 w-[120px] lg:w-[180px] lg:flex-initial flex justify-center items-center'>
									<div className='text-left text-black text-md uppercase leading-5 tracking-widest'>
										Internationally Recognised Technique
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			<section className='md:hidden bg-[#ddd9d6] pt-6 pb-20'>
				<div className='container flex flex-row justify-between items-center gap-3'>
					<div className='flex-1 text-center'>
						<div className='text-xxs uppercase leading-4 tracking-wider'>
							<span className='block text-xl tracking-widest'>15+</span>
							Years of Experience
						</div>
					</div>
					<div className='w-[1px] h-[50px] bg-[#141A4C]/30 shrink-0' />
					<div className='flex-1 text-center'>
						<div className='text-xxs uppercase leading-4 tracking-wider'>
							<span className='block text-xl tracking-widest'>1,000+</span>
							Five-Star Patient Outcomes
						</div>
					</div>
					<div className='w-[1px] h-[50px] bg-[#141A4C]/30 shrink-0' />
					<div className='flex-1 text-center'>
						<div className='text-xxs uppercase leading-4 tracking-wider'>
							Internationally Recognised Technique
						</div>
					</div>
				</div>
			</section>
		</>
	)
}